import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, BookMarked, X } from 'lucide-react';
import { api } from '../api/client';
import type { GuidanceStep, RecordingTemplate } from '@databro/shared';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@databro/ui';

export default function Recipes() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RecordingTemplate | 'new' | null>(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['recordingTemplates'],
    queryFn: () => api.getRecordingTemplates(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteRecordingTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recordingTemplates'] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/recordings">
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Recipes</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Reusable capture filters + guided steps. Pick one in the extension side panel to redo a
              flow.
            </p>
          </div>
        </div>
        <Button onClick={() => setEditing('new')}>
          <Plus className="mr-2 size-4" />
          New recipe
        </Button>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (templates ?? []).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-12 text-center">
            <BookMarked className="size-8 text-muted-foreground/50" />
            <p className="font-medium">No recipes yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Save one from the extension side panel, or create it here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {(templates ?? []).map((t) => (
            <Card key={t.id} className="group">
              <CardHeader className="flex-row items-start justify-between space-y-0 pb-2">
                <CardTitle className="text-base">{t.name}</CardTitle>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="size-8" onClick={() => setEditing(t)}>
                    <span className="text-xs">Edit</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 opacity-0 group-hover:opacity-100"
                    onClick={() => {
                      if (confirm(`Delete recipe "${t.name}"?`)) deleteMutation.mutate(t.id);
                    }}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex flex-wrap gap-1">
                  {t.platform && <Badge variant="secondary">{t.platform}</Badge>}
                  {t.replayable && <Badge variant="outline">replayable</Badge>}
                  {t.actionId && (
                    <Link to={`/actions/${t.actionId}`}>
                      <Badge>linked action</Badge>
                    </Link>
                  )}
                </div>
                {t.focusPatterns.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {t.focusPatterns.map((p) => (
                      <code key={p} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        {p}
                      </code>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {t.guidanceSteps.length} step{t.guidanceSteps.length === 1 ? '' : 's'}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <RecipeDialog
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['recordingTemplates'] });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function RecipeDialog({
  template,
  onClose,
  onSaved,
}: {
  template: RecordingTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(template?.name ?? '');
  const [platform, setPlatform] = useState<string>(template?.platform ?? 'custom');
  const [focusPatterns, setFocusPatterns] = useState<string[]>(template?.focusPatterns ?? ['']);
  const [steps, setSteps] = useState<GuidanceStep[]>(template?.guidanceSteps ?? [{ title: '' }]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        platform: platform as 'shopee' | 'tiktok' | 'custom',
        focusPatterns: focusPatterns.map((p) => p.trim()).filter(Boolean),
        guidanceSteps: steps.filter((s) => s.title.trim()),
        replayable: template?.replayable ?? false,
      };
      return template
        ? api.updateRecordingTemplate(template.id, payload)
        : api.createRecordingTemplate(payload);
    },
    onSuccess: onSaved,
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit recipe' : 'New recipe'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Shopee store products" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Platform</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
              >
                <option value="shopee">shopee</option>
                <option value="tiktok">tiktok</option>
                <option value="custom">custom</option>
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Focus patterns (URL globs)</Label>
            {focusPatterns.map((p, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={p}
                  onChange={(e) => setFocusPatterns((prev) => prev.map((x, xi) => (xi === i ? e.target.value : x)))}
                  placeholder="*rcmd_items*"
                  className="font-mono text-xs"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setFocusPatterns((prev) => prev.filter((_, xi) => xi !== i))}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setFocusPatterns((prev) => [...prev, ''])}>
              <Plus className="mr-1.5 size-3.5" /> Add pattern
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Guided steps</Label>
            {steps.map((s, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={s.title}
                  onChange={(e) => setSteps((prev) => prev.map((x, xi) => (xi === i ? { ...x, title: e.target.value } : x)))}
                  placeholder="Scroll to load products"
                />
                <Input
                  value={s.matchPattern ?? ''}
                  onChange={(e) => setSteps((prev) => prev.map((x, xi) => (xi === i ? { ...x, matchPattern: e.target.value } : x)))}
                  placeholder="auto-check: *rcmd_items*"
                  className="font-mono text-xs"
                />
                <Button variant="ghost" size="icon" onClick={() => setSteps((prev) => prev.filter((_, xi) => xi !== i))}>
                  <X className="size-4" />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setSteps((prev) => [...prev, { title: '' }])}>
              <Plus className="mr-1.5 size-3.5" /> Add step
            </Button>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!name.trim() || saveMutation.isPending}>
              Save recipe
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
